import { useReducer } from "react";
import i18next from "i18next";
import ScaleContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../utils/helpers/execute";
import EndPoints from "../../services/end-points";
import { IScaleDetails } from "../../models/scale/response";
import { ICreateScale, IUpdateScale } from "../../models/scale/request";
import { IScaleMovementQuery, IScaleQuery } from "../../models/scale/query";
import { getApiErrorDataMessage } from "../../utils/api/get-api-error-message";
import { errorNotification } from "../../utils/helpers/notification";

export interface IProps {
  children: React.ReactNode;
}

/** The pending-changes routes wrap their payload in `data` while most single
 *  object routes in this dashboard answer bare, so both shapes are accepted. */
const unwrap = (payload: any): any =>
  payload && typeof payload === "object" && !Array.isArray(payload) && "data" in payload
    ? payload.data
    : payload;

/** With `responseType: "blob"` a 422 body arrives as a blob too, so reading it
 *  as text is the only way the user sees the listed offending products. */
const readBlobErrorMessage = async (error: any): Promise<string> => {
  const fallback = i18next.t("server_error_try_again");
  const data = error?.response?.data;

  if (!(data instanceof Blob)) {
    return getApiErrorDataMessage(data, fallback);
  }

  try {
    const text = await data.text();
    return getApiErrorDataMessage(JSON.parse(text), fallback);
  } catch {
    return fallback;
  }
};

/** The file name carries the store and the timestamp, and it is what the audit
 *  trail records — so it is read from the response, never rebuilt here. */
const getFileNameFromHeaders = (headers: any, fallback: string): string => {
  const disposition =
    headers?.["content-disposition"] ?? headers?.["Content-Disposition"];

  if (typeof disposition !== "string") return fallback;

  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // Fall through to the plain form below.
    }
  }

  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain?.[1]?.trim() || fallback;
};

const ScaleContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.scale.getAllScales(state.query);

        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });

        const { data } = await EndPoints.scale.getScale(id);
        dispatch({ type: "SET_DETAILSS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const setDetails = async (data?: IScaleDetails) => {
    dispatch({ type: "SET_DETAILSS", payload: { details: data } });
  };

  const createScale = async (request: ICreateScale) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });

        await EndPoints.scale.createScale(request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
      },
      throwException: true,
    });
  };

  const updateScale = async (id: number, request: IUpdateScale) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });

        await EndPoints.scale.updateScale(id, request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const deleteScale = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });

        await EndPoints.scale.deleteScale(id);
        getData();
      },
      fallback: (error) => {
        errorNotification(
          i18next.t("error"),
          getApiErrorDataMessage(
            error?.response?.data,
            i18next.t("server_error_try_again"),
          ),
        );
      },
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
      },
      // The refusal is reported above. Keeping it contained prevents AntD's
      // confirmation promise from closing on an unhandled rejection.
      throwException: false,
    });
  };

  const getSlots = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "slots" } });

        const { data } = await EndPoints.scale.getScaleSlots(id);

        dispatch({ type: "SET_SLOTS", payload: { slots: data?.data ?? [] } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "slots" } });
      },
      throwException: false,
    });
  };

  const detachProduct = async (id: number, productId: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "detach" } });

        await EndPoints.scale.detachScaleProduct(id, productId);
        // The freed slot changes the file, so the badge has to catch up too.
        await getSlots(id);
        await getPendingChanges(id);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "detach" } });
      },
      throwException: true,
    });
  };

  const getMovements = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "movements" } });

        const { data } = await EndPoints.scale.getScaleMovements(
          id,
          state.movementsQuery,
        );

        dispatch({ type: "SET_MOVEMENTS", payload: { movements: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "movements" } });
      },
      throwException: false,
    });
  };

  const setMovementsQuery = (query: IScaleMovementQuery) => {
    dispatch({ type: "SET_MOVEMENTS_QUERY", payload: { query } });
  };

  const getPendingChanges = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "pending" } });

        const { data } = await EndPoints.scale.getScalePendingChanges(id);

        dispatch({
          type: "SET_PENDING_CHANGES",
          payload: { pendingChanges: unwrap(data) },
        });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "pending" } });
      },
      throwException: false,
    });
  };

  /** One call for the whole page; the endpoint caps it at 100 scales. */
  const getListPendingChanges = async (scaleIds: number[]) => {
    if (scaleIds.length === 0) return;

    await execute({
      callback: async () => {
        const { data } = await EndPoints.scale.getScalesPendingChanges(
          scaleIds.slice(0, 100),
        );

        const summaries = unwrap(data);

        dispatch({
          type: "SET_PENDING_SUMMARIES",
          payload: { summaries: Array.isArray(summaries) ? summaries : [] },
        });
      },
      fallback: (error) => {},
      throwException: false,
    });
  };

  const exportScale = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "export" } });

        const response = await EndPoints.scale.exportScale(id);

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          getFileNameFromHeaders(response.headers, `${id}.xlsx`),
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // Downloading only arms the confirmation; the badge stays lit.
        await getDetails(id);
      },
      fallback: async (error) => {
        errorNotification(
          i18next.t("export_scale_file"),
          await readBlobErrorMessage(error),
        );
      },
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "export" } });
      },
      throwException: false,
    });
  };

  const markFed = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "markFed" } });

        const { data } = await EndPoints.scale.markScaleFed(id);

        dispatch({ type: "SET_DETAILSS", payload: { details: data } });
        // The snapshot is the moment of download, so the count drops only now.
        await getPendingChanges(id);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "markFed" } });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IScaleQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  return (
    <ScaleContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDetails,
          setDetails,

          createScale,
          updateScale,
          deleteScale,

          getSlots,
          detachProduct,

          getMovements,
          setMovementsQuery,

          getPendingChanges,
          getListPendingChanges,

          exportScale,
          markFed,

          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </ScaleContext.Provider>
  );
};

export default ScaleContextProvider;
